import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sprout, Handshake, Timer, Globe2, Users } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useTitle } from "@/lib/useTitle";

const values = [
  { icon: ShieldCheck, title: "Quality First", desc: "Buyer-approved specifications, verified across every stage." },
  { icon: Sprout, title: "Reliable Supply", desc: "Consistent bulk availability from trusted origin partners." },
  { icon: Handshake, title: "Transparent Communication", desc: "Clear documentation, honest updates, no hidden clauses." },
  { icon: Users, title: "Customer Focus", desc: "Adapting specifications and packaging to buyer requirements." },
  { icon: Timer, title: "Timely Execution", desc: "Structured processing and dispatch coordination." },
  { icon: Globe2, title: "Long-Term Partnerships", desc: "Building repeat trade with international buyers." },
];

const About = () => {
  useTitle("About Us | Indian Agro Export Partner — GN Agro Traders",
    "GN Agro Traders (Grewal & Namburu Agro Traders LLP) — a professional Indian agro trading and export supply partner for rice, pulses, wheat and agro commodities.");
  return (<div>
    <section className="bg-[#004225] text-white grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />About GN Agro Traders</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl">Connecting Indian Farms to Global Markets</h1>
        <p className="mt-6 max-w-3xl text-white/85">A brand of Grewal & Namburu Agro Traders LLP — a professional Indian agro trading and export supply partner focused on premium Basmati and Non-Basmati Rice, Pulses, Wheat, Flour and other agricultural commodities.</p>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10">
      <Reveal>
        <div className="text-[11px] font-eyebrow text-[#B5942D]">Who We Are</div>
        <h2 className="mt-2 font-display text-3xl text-[#004225]">A B2B Agricultural Export Partner</h2>
        <p className="mt-4 text-[15px] text-[#171717]/80 leading-relaxed">GN Agro Traders operates as a professional sourcing and export company. We work with trusted Indian farms, mills and processing partners to deliver export-quality agricultural products to importers, distributors, wholesalers, food companies and private-label buyers around the world.</p>
      </Reveal>
      <Reveal delay={120}>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="text-[11px] font-eyebrow text-[#B5942D]">Our Mission</div>
            <p className="mt-2 text-[14px] text-[#171717]/80">To supply consistent, export-grade Indian agricultural commodities to global buyers with transparency and reliability.</p>
          </div>
          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="text-[11px] font-eyebrow text-[#B5942D]">Our Vision</div>
            <p className="mt-2 text-[14px] text-[#171717]/80">To be a preferred Indian agri-export partner for buyers seeking quality, scale and service.</p>
          </div>
          <div className="bg-white border border-[#004225]/10 rounded-sm p-6 sm:col-span-2">
            <div className="text-[11px] font-eyebrow text-[#B5942D]">Supply Network</div>
            <p className="mt-2 text-[14px] text-[#171717]/80">Sourcing across India&apos;s key agri belts: Punjab, Haryana, MP, Maharashtra, Andhra Pradesh, Telangana and beyond — with export coordination via Mundra and Nhava Sheva ports.</p>
          </div>
        </div>
      </Reveal>
    </section>

    <section className="bg-white border-y border-[#004225]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />Our Values</div>
        <h2 className="mt-2 font-display text-3xl text-[#004225]">Values Global Buyers Can Rely On</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 40}>
              <div className="bg-[#F4F4ED] rounded-sm border border-[#004225]/10 p-6 h-full">
                <v.icon className="h-5 w-5 text-[#D4AF37]" />
                <div className="mt-2 font-display text-lg text-[#004225]">{v.title}</div>
                <p className="mt-2 text-[13px] text-[#171717]/75">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="bg-[#002B18] text-white rounded-sm p-10 sm:p-14">
        <div className="text-[11px] font-eyebrow text-[#D4AF37]">Global Buyer Partnership</div>
        <h3 className="mt-2 font-display text-3xl">Ready to discuss your requirement?</h3>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/request-quote" className="rounded-full btn-gold px-6 py-3 text-xs font-semibold uppercase tracking-widest">Request a Quote</Link>
          <Link to="/products" className="rounded-full border border-white/30 text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-white/10">Explore Products</Link>
        </div>
      </div>
    </section>
  </div>
  );
};

export default About;
